// Shared constants for the drought reporting page: CDI class colours and the
// indicator comparison-row configuration. Keeping these in one place means the
// AI-note routing, the maps, and the charts never drift apart.
//
// Each map renders as a static PNG. The reliable path is a direct GeoServer WMS
// GetMap image (`wms`); where the chatbot's composited map is available the
// `/api/data_api/reporting/map/` endpoint (addressed by `indicator` + time step)
// is swapped in for the nicer legend/title framing.

import { base_url } from "../Geoportal";

const GEOSERVER_WMS = `${base_url}/geoserver/wms`;
const WORKSPACE = "africa_drought_datasets";

// CDI severity classes, ordered least → most severe. Colours match the map SLDs
// and the existing stacked-bar charts so the page reads as one product.
export const CDI_CLASSES = [
  { key: "Watch", label: "Watch", color: "#FFC12B" },
  { key: "Warning", label: "Warning", color: "#F58220" },
  { key: "Alert", label: "Alert", color: "#E23B2E" },
];

export const NO_DROUGHT_COLOR = "#D9D9D9";

function pad2(n) {
  return String(n).padStart(2, "0");
}

// The GeoServer view name + CQL for a resolved map request.
function wmsLayerFor(indicator, { year, month, dekad, timescale, forecastPeriod }) {
  const mm = pad2(month);
  switch (indicator) {
    case "cdi":
      return { layer: `${WORKSPACE}:grid_01dd_cdi_${mm}${pad2(dekad)}_view`, cql: `year=${year}` };
    case "spi":
      return { layer: `${WORKSPACE}:grid_1dd_spi_${mm}${pad2(timescale)}_view`, cql: `year=${year}` };
    case "spi_era5":
      return {
        layer: `${WORKSPACE}:grid_025dd_spi_${mm}${pad2(dekad)}_${pad2(timescale)}_view`,
        cql: `year=${year}`,
      };
    case "sma":
      return { layer: `${WORKSPACE}:grid_01dd_sma_anomaly_${mm}${pad2(dekad)}_view`, cql: `year=${year}` };
    case "fapar_anomaly":
      return {
        layer: `${WORKSPACE}:grid_083dd_fapar_anomaly_${mm}${pad2(dekad)}_view`,
        cql: `year=${year}`,
      };
    case "unwdc":
      return { layer: `${WORKSPACE}:grid_1dd_unwdc_${mm}${pad2(timescale)}_view`, cql: `year=${year}` };
    case "longrange_forecast":
      if (!forecastPeriod) return null;
      return {
        layer: `${WORKSPACE}:data_api_forecastpolygon`,
        cql: `year=${year} AND forecast_period='${forecastPeriod}'`,
      };
    default:
      return null;
  }
}

/**
 * Build a direct WMS GetMap PNG URL for a resolved map request.
 *
 * @param {{indicator, year, month, dekad?, timescale?}} req
 * @param {{minx, miny, maxx, maxy}|null} bounds Country bbox; null → Africa.
 * @param {number} width
 * @param {number} height
 * @returns {string|null}
 */
// 4:3 to match both the MapGenerator composite's own default render size and
// the aspect-ratio: 4/3 boxes every map sits in — html2canvas (used for the
// PDF export) ignores CSS object-fit and just stretches the raw image into
// its box, so a mismatched source ratio shows up as a visibly stretched map
// in the exported PDF even though real browsers letterbox it correctly.
export function wmsMapUrl(req, bounds, width = 800, height = 600) {
  if (!req) return null;
  const spec = wmsLayerFor(req.indicator, req);
  if (!spec) return null;
  const bb = bounds || { minx: -26, miny: -38, maxx: 58, maxy: 40 };
  const params = new URLSearchParams({
    service: "WMS",
    version: "1.1.1",
    request: "GetMap",
    layers: spec.layer,
    bbox: `${bb.minx},${bb.miny},${bb.maxx},${bb.maxy}`,
    width: String(width),
    height: String(height),
    srs: "EPSG:4326",
    format: "image/png",
    transparent: "true",
    CQL_FILTER: spec.cql,
  });
  return `${GEOSERVER_WMS}?${params.toString()}`;
}

/**
 * Turn one `maps[]` entry into a resolved map request for a given time step.
 *
 * @param {object} map A `maps[]` entry from `INDICATOR_ROWS`.
 * @param {object} ctx `{ year, monthInt, dekad, prevSteps: [...], spiGpccStep: {year, monthInt} }`
 * @returns {{indicator, year, month, dekad?, timescale?, unavailable?: boolean}|null}
 */
export function buildMapRequest(map, ctx) {
  const { year, monthInt, dekad, prevSteps = [], spiGpccStep, forecastPeriod } = ctx;
  if (!map.mapIndicator || !monthInt) return null;

  const req = { indicator: map.mapIndicator, unavailable: Boolean(map.mapUnavailable) };

  if (map.mapIndicator === "cdi") {
    const step = prevSteps[(map.step || 1) - 1];
    if (!step) return null;
    return { ...req, year: Number(step.year), month: Number(step.monthInt), dekad: Number(step.dekad) };
  }
  if (map.mapIndicator === "spi") {
    // GPCC SPI feeding the CDI lags the analysis period by one calendar month.
    const s = spiGpccStep || { year, monthInt };
    return { ...req, year: Number(s.year), month: Number(s.monthInt), timescale: map.timescale };
  }
  if (map.mapIndicator === "spi_era5") {
    // ERA5 SPI is on the 0.25° grid and published per dekad; always 1st dekad.
    return { ...req, year: Number(year), month: Number(monthInt), dekad: 1, timescale: map.timescale };
  }
  if (map.mapIndicator === "unwdc") {
    return { ...req, year: Number(year), month: Number(monthInt), timescale: map.timescale };
  }
  if (map.mapIndicator === "longrange_forecast") {
    return { ...req, year: Number(year), month: Number(monthInt), forecastPeriod };
  }
  // sma / fapar_anomaly: the analysis dekad and year.
  return { ...req, year: Number(year), month: Number(monthInt), dekad: Number(dekad) };
}

// Supporting-indicator section: each row pairs two related maps, and every map
// carries its own stacked-bar time series (`chart` + `seriesParam`/`seriesKey`,
// the reporting-stats dataset and response field) plus an interpretation. Notes
// are AI narrative blocks: `sharedNoteKeys` render once below the row, a map's
// own `noteKey` renders under that column. A map with `detailNoteKey` (and no
// `chart`) shows a longer analysis panel in place of a chart. Fallback text
// (block name minus `_note`) lives in fallbackNarrative.js.
export const INDICATOR_ROWS = [
  {
    id: "cdi_recent",
    title: "Combined Drought Index over the last two dekads",
    sharedNoteKeys: [],
    maps: [
      {
        key: "cdi_prev_1", mapIndicator: "cdi", step: 1, label: "Previous dekad",
        chart: "cdi", seriesParam: "cdi", seriesKey: "cdi_summary",
      },
      {
        // Same time series as the left column, so use the space for detail.
        key: "cdi_prev_2", mapIndicator: "cdi", step: 2, label: "Two dekads back",
        detailNoteKey: "cdi_trend_note",
      },
    ],
  },
  {
    id: "spi",
    title: "SPI-3 rainfall deficit, two sources",
    // Same index, two independent inputs: GPCC gauge data (one month behind the
    // analysis period, as it feeds the CDI) and ERA5 reanalysis (near real time).
    sharedNoteKeys: ["spi_trend_note"],
    maps: [
      {
        key: "spi_gpcc", mapIndicator: "spi", timescale: 3,
        label: "SPI-3 · GPCC (one month behind)",
        chart: "spi", seriesParam: "spi_03", seriesKey: "spi_03_summary",
      },
      {
        key: "spi_era5", mapIndicator: "spi_era5", timescale: 3,
        label: "SPI-3 · ERA5 reanalysis",
        chart: "spi", seriesParam: "spi_era5_03", seriesKey: "spi_03_era5_summary",
      },
    ],
  },
  {
    id: "veg_soil",
    title: "Vegetation and soil moisture anomalies",
    // One combined read: soil moisture leads, vegetation follows.
    sharedNoteKeys: ["veg_soil_note"],
    maps: [
      {
        key: "sma", mapIndicator: "sma",
        // The SMA GeoServer layer is not published yet; show "map failed to
        // load" until it is (drop this flag then).
        mapUnavailable: true,
        label: "Soil moisture anomaly · root zone",
        chart: "sma", seriesParam: "sma", seriesKey: "sma",
      },
      {
        key: "fapar", mapIndicator: "fapar_anomaly",
        label: "fAPAR anomaly · vegetation",
        chart: "fapar", seriesParam: "fapar_anomaly", seriesKey: "fapar_anomaly_summary",
      },
    ],
  },
  {
    id: "outlook",
    title: "Seasonal outlook and unusual conditions",
    // Forward-looking: the ACMAD long-range forecast for the coming season next
    // to the UNWDC wet/dry extremes. (Drought-forecast-to-persist layer to be
    // added here later.)
    sharedNoteKeys: ["outlook_note"],
    maps: [
      {
        key: "forecast", mapIndicator: "longrange_forecast",
        label: "ACMAD seasonal rainfall forecast",
        chart: "forecast", seriesParam: "longrange_forecast", seriesKey: "forecast_summary",
      },
      {
        key: "unwdc", mapIndicator: "unwdc", timescale: 3,
        label: "Unusually dry / wet conditions · 3-month",
        chart: "unwdc", seriesParam: "unwdc_03", seriesKey: "unwdc_03_summary",
      },
    ],
  },
];

// Distinct reporting-stats datasets the indicator charts need, minus `cdi`
// (already fetched for the headline time series). Keyed param → response field.
export const INDICATOR_SERIES = Object.fromEntries(
  INDICATOR_ROWS.flatMap((row) => row.maps)
    .filter((m) => m.seriesParam && m.seriesParam !== "cdi")
    .map((m) => [m.seriesParam, m.seriesKey]),
);
